import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users } from 'lucide-react';
import type { PyramidaUiState } from '../hooks/usePyramidaState';
import { formatTon } from '../lib/format';

interface StatusStripProps {
  state: PyramidaUiState;
  onOpen: () => void;
}

function useNowSec(): number {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/** Compact always-on Pyramida ticker shown at the top of every screen:
 *  bank, players and time to draw. Tapping it jumps to the GRAM table. */
export function StatusStrip({ state, onOpen }: StatusStripProps) {
  const { t } = useTranslation();
  const now = useNowSec();
  const left = Math.max(0, state.lottery.roundEndTime - now);
  const d = Math.floor(left / 86400);
  const h = Math.floor((left % 86400) / 3600);
  const m = Math.floor((left % 3600) / 60);
  const s = left % 60;
  const timer =
    d > 0
      ? `${d}${t('v2.lobby.days').slice(0, 1)} ${h}${t('v2.lobby.hours').slice(0, 1)}`
      : h > 0
        ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
        : `${m}:${String(s).padStart(2, '0')}`;

  return (
    <button type="button" className="pstrip" onClick={onOpen}>
      <img src="/prmd-logo.webp" alt="" className="pstrip__logo" draggable={false} />
      <span className="pstrip__name">PYRAMIDA</span>
      <span className="pstrip__bank">
        {state.lastUpdated === null ? '···' : `${formatTon(state.lottery.prizePool)} GRAM`}
      </span>
      <span className="pstrip__spacer" />
      <span className="pstrip__players">
        <Users size={11} aria-hidden="true" />
        {state.lottery.participantsCount}
      </span>
      <span className="pstrip__timer">{timer}</span>
      <span className={`live-dot ${state.isLive ? 'live-dot--on' : 'live-dot--off'}`} />
    </button>
  );
}
